import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../api/api';
import SeasonSelector from '../../components/analytics/SeasonSelector';

const flagColors = {
  missing_stats: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800', border: 'border-yellow-300' },
  zeroed_stats: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-800', border: 'border-orange-300' },
  blowout: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800', border: 'border-red-300' },
  high_scoring: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-800', border: 'border-purple-300' },
};

const flagIcons = {
  missing_stats: '⚠️',
  zeroed_stats: '🟡',
  blowout: '💥',
  high_scoring: '🔥',
};

const ComplianceAnalytics = () => {
  const { t } = useTranslation();
  const [seasonId, setSeasonId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!seasonId) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await api.get(`/analytics/compliance?seasonId=${seasonId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch (err) { setData(null); }
      setLoading(false);
    };
    fetch();
  }, [seasonId]);

  const filteredFlags = data?.flaggedGames?.filter(f => filter === 'all' || f.flag === filter) || [];
  const flagCounts = data?.flaggedGames?.reduce((acc, f) => { acc[f.flag] = (acc[f.flag] || 0) + 1; return acc; }, {}) || {};

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200">🛡️ {t('analytics.compliance.title')}</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">{t('analytics.compliance.subtitle')}</p>
        </div>
        <SeasonSelector selectedSeasonId={seasonId} onSeasonChange={setSeasonId} />
      </div>

      {loading && <div className="text-center py-12 text-gray-500 dark:text-gray-400">{t('common.loading')}</div>}

      {data && !loading && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {['missing_stats', 'zeroed_stats', 'blowout', 'high_scoring'].map(flag => (
              <button
                key={flag}
                onClick={() => setFilter(prev => prev === flag ? 'all' : flag)}
                className={`p-4 rounded-xl border-2 transition text-left ${
                  filter === flag
                    ? `${flagColors[flag].bg} ${flagColors[flag].border}`
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:border-gray-600'
                }`}
              >
                <p className="text-2xl font-bold">{flagCounts[flag] || 0}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{flagIcons[flag]} {t(`analytics.compliance.flags.${flag}`)}</p>
              </button>
            ))}
          </div>

          {/* Flagged Games Table */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-x-auto mb-6">
            <div className="p-6 pb-3 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-700 dark:text-gray-300">
                {t('analytics.compliance.flaggedGames')} ({filteredFlags.length})
              </h3>
              {filter !== 'all' && (
                <button onClick={() => setFilter('all')} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                  {t('analytics.compliance.showAll')}
                </button>
              )}
            </div>
            {filteredFlags.length === 0 ? (
              <p className="px-6 pb-6 text-gray-500 dark:text-gray-400">{t('analytics.compliance.noFlags')}</p>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('analytics.compliance.flag')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('analytics.cols.date')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('analytics.compliance.matchup')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('analytics.cols.score')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('analytics.compliance.details')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFlags.map((f, i) => {
                    const colors = flagColors[f.flag] || flagColors.missing_stats;
                    return (
                      <tr key={i} className="border-b hover:bg-gray-50 dark:bg-gray-800">
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${colors.bg} ${colors.text}`}>
                            {flagIcons[f.flag]} {t(`analytics.compliance.flags.${f.flag}`)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{f.date ? new Date(f.date).toLocaleDateString() : '-'}</td>
                        <td className="px-4 py-3 text-sm font-medium">{f.homeTeam} vs {f.awayTeam}</td>
                        <td className="px-4 py-3 text-sm">{f.homeScore} - {f.awayScore}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{f.detail}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Team Anomalies */}
          {data.teamAnomalies?.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
              <h3 className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-4">⚡ {t('analytics.compliance.teamAnomalies')}</h3>
              <div className="space-y-3">
                {data.teamAnomalies.map((a, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                    <span className="text-lg">🚨</span>
                    <div>
                      <p className="font-semibold text-gray-800 dark:text-gray-200">{a.team}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{a.reason}</p>
                      {a.avgScoring && <p className="text-xs text-gray-400">{t('analytics.cols.ppg')}: {a.avgScoring}</p>}
                      {a.blowoutPct && <p className="text-xs text-gray-400">{t('analytics.compliance.blowoutPct')}: {a.blowoutPct}%</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ComplianceAnalytics;
